import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { FeedbackService, FeedbackPayload } from '../../services/feedback.service';
import html2canvas from 'html2canvas';
import { JiraService } from '../../services/jira.service';
import { AuthService } from '../../services/auth.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface ConsoleLog {
  type: string;
  data: string;
  timestamp: number;
}

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  providers: [FeedbackService, JiraService],
  template: `
    <div class="feedback-widget" *ngIf="isAuthenticated$ | async">
      <button (click)="toggleWidget()" class="feedback-trigger">
        Feedback
      </button>

      <div class="feedback-form" *ngIf="isWidgetOpen">
        <form [formGroup]="feedbackForm" (ngSubmit)="onSubmit()">
          <h2>Send Feedback</h2>
          
          <div class="form-group">
            <label for="type">Type</label>
            <select id="type" formControlName="type" class="form-control">
              <option value="">Select type</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="improvement">Improvement</option>
            </select>
          </div>

          <div class="form-group">
            <label for="title">Title</label>
            <input id="title" type="text" formControlName="title" 
                   class="form-control" placeholder="Brief description">
          </div>

          <div class="form-group">
            <label for="description">Description</label>
            <textarea id="description" formControlName="description" 
                     class="form-control" rows="4" 
                     placeholder="Please provide details..."></textarea>
          </div>

          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" formControlName="includeScreenshot">
              Include screenshot
            </label>
            <label class="checkbox-label">
              <input type="checkbox" formControlName="includeConsoleLog">
              Include console logs
            </label>
          </div>

          <div class="screenshot-preview" *ngIf="screenshotData">
            <img [src]="screenshotData" alt="Screenshot preview" class="preview-image">
            <button type="button" class="btn btn-text" (click)="removeScreenshot()">
              <i class="fas fa-times"></i> Remove
            </button>
          </div>

          <div class="console-preview" *ngIf="consoleData.length > 0">
            <div class="console-data">
              <pre>{{ consoleData | json }}</pre>
            </div>
            <button type="button" class="btn btn-text" (click)="removeConsoleLogs()">
              <i class="fas fa-times"></i> Remove
            </button>
          </div>

          <div class="actions">
            <button type="button" class="btn btn-secondary" (click)="toggleWidget()">Cancel</button>
            <button type="submit" class="btn btn-primary" 
                    [disabled]="feedbackForm.invalid || isSubmitting">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styles: [`
    .feedback-widget {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1000;
    }

    .feedback-trigger {
      position: relative;
      z-index: 1001;
      padding: 8px 16px;
      border-radius: 20px;
      background: #2c3e50;
      border: none;
      color: white;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 2px 5px rgba(0,0,0,0.2);
      transition: all 0.2s ease;
    }

    .feedback-trigger:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
      background: #34495e;
    }

    .feedback-form {
      position: absolute;
      bottom: 60px;
      right: 0;
      width: 350px;
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      border: 1px solid #eee;
    }

    h2 {
      margin: 0 0 20px;
      font-size: 1.2rem;
      color: #2c3e50;
    }

    .form-group {
      margin-bottom: 15px;
    }

    .checkbox-group {
      display: flex;
      gap: 20px;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #2c3e50;
      font-size: 0.9rem;
      cursor: pointer;
    }

    .checkbox-label input {
      margin: 0;
    }

    label {
      display: block;
      margin-bottom: 5px;
      color: #2c3e50;
      font-size: 0.9rem;
      font-weight: 500;
    }

    .form-control {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 0.9rem;
      background: #f8f9fa;
    }

    .form-control:focus {
      outline: none;
      border-color: #2c3e50;
      box-shadow: 0 0 0 2px rgba(44, 62, 80, 0.1);
      background: white;
    }

    textarea.form-control {
      resize: vertical;
      min-height: 80px;
    }

    .screenshot-preview, .console-preview {
      margin: 10px 0;
      padding: 10px;
      background: #f8f9fa;
      border-radius: 4px;
      border: 1px solid #eee;
    }

    .preview-image {
      width: 100%;
      height: auto;
      border-radius: 4px;
      margin-bottom: 8px;
    }

    .console-data {
      max-height: 150px;
      overflow-y: auto;
      background: #2c3e50;
      color: #fff;
      padding: 8px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      margin-bottom: 8px;
    }

    .console-data pre {
      margin: 0;
      white-space: pre-wrap;
    }

    .btn-text {
      background: none;
      border: none;
      color: #2c3e50;
      padding: 4px 8px;
      font-size: 0.8rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .btn-text:hover {
      color: #e74c3c;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 20px;
    }

    .btn {
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 0.9rem;
      cursor: pointer;
      border: none;
      transition: all 0.2s ease;
      font-weight: 500;
    }

    .btn-primary {
      background: #2c3e50;
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: #34495e;
      transform: translateY(-1px);
    }

    .btn-primary:disabled {
      background: #95a5a6;
      cursor: not-allowed;
    }

    .btn-secondary {
      background: #95a5a6;
      color: white;
    }

    .btn-secondary:hover {
      background: #7f8c8d;
      transform: translateY(-1px);
    }

    .error-message {
      color: #e74c3c;
      font-size: 0.8rem;
      margin-top: 5px;
    }
  `]
})
export class FeedbackComponent implements OnInit {
  feedbackForm: FormGroup;
  isWidgetOpen = false;
  isSubmitting = false;
  errorMessage = '';
  screenshotData: string | null = null;
  consoleData: ConsoleLog[] = [];
  isAuthenticated$: Observable<boolean>;

  private originalConsole: any = {};
  private capturedLogs: ConsoleLog[] = [];

  constructor(
    private fb: FormBuilder,
    private feedbackService: FeedbackService,
    private jiraService: JiraService,
    private authService: AuthService
  ) {
    this.feedbackForm = this.fb.group({
      type: ['', Validators.required],
      title: ['', Validators.required],
      description: ['', Validators.required],
      includeScreenshot: [false],
      includeConsoleLog: [false]
    });

    // Check if user is authenticated
    this.isAuthenticated$ = this.authService.user$.pipe(
      map(user => !!user)
    );

    // Listen for form control changes
    this.feedbackForm.get('includeScreenshot')?.valueChanges.subscribe(include => {
      if (include) {
        this.captureScreenshot();
      } else {
        this.removeScreenshot();
      }
    });

    this.feedbackForm.get('includeConsoleLog')?.valueChanges.subscribe(include => {
      if (include) {
        this.startCapturingConsoleLogs();
      } else {
        this.stopCapturingConsoleLogs();
      }
    });
  }

  ngOnInit(): void {
    // Store original console methods
    this.originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info
    };
  }

  private getCircularReplacer() {
    const seen = new WeakSet();
    return (key: any, value: any) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack
        };
      }
      if (value instanceof HTMLElement) {
        return `[${value.tagName.toLowerCase()} Element]`;
      }
      return value;
    };
  }

  private safeStringify(obj: any): string {
    try {
      return JSON.stringify(obj, this.getCircularReplacer(), 2);
    } catch (error) {
      return String(obj);
    }
  }

  private startCapturingConsoleLogs(): void {
    this.capturedLogs = [];
    this.consoleData = []; // Clear existing console data

    // Override console methods to capture logs
    console.log = (...args: any[]) => {
      const logData = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, this.getCircularReplacer());
        }
        return String(arg);
      }).join(' ');

      this.capturedLogs.push({
        type: 'log',
        data: logData,
        timestamp: Date.now()
      });
      this.originalConsole.log.apply(console, args);
    };

    console.error = (...args: any[]) => {
      const logData = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, this.getCircularReplacer());
        }
        return String(arg);
      }).join(' ');

      this.capturedLogs.push({
        type: 'error',
        data: logData,
        timestamp: Date.now()
      });
      this.originalConsole.error.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      const logData = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, this.getCircularReplacer());
        }
        return String(arg);
      }).join(' ');

      this.capturedLogs.push({
        type: 'warn',
        data: logData,
        timestamp: Date.now()
      });
      this.originalConsole.warn.apply(console, args);
    };

    console.info = (...args: any[]) => {
      const logData = args.map(arg => {
        if (typeof arg === 'object') {
          return JSON.stringify(arg, this.getCircularReplacer());
        }
        return String(arg);
      }).join(' ');

      this.capturedLogs.push({
        type: 'info',
        data: logData,
        timestamp: Date.now()
      });
      this.originalConsole.info.apply(console, args);
    };

    // Update the console preview data
    this.consoleData = this.capturedLogs;
  }

  private stopCapturingConsoleLogs(): void {
    // Restore original console methods
    console.log = this.originalConsole.log;
    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    console.info = this.originalConsole.info;
    this.capturedLogs = [];
  }

  toggleWidget(): void {
    this.isWidgetOpen = !this.isWidgetOpen;
    if (!this.isWidgetOpen) {
      this.resetForm();
    }
  }

  async captureScreenshot(): Promise<void> {
    try {
      const canvas = await html2canvas(document.body);
      this.screenshotData = canvas.toDataURL();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      this.feedbackForm.patchValue({ includeScreenshot: false });
    }
  }

  removeScreenshot(): void {
    this.screenshotData = null;
    this.feedbackForm.patchValue({ includeScreenshot: false });
  }

  async onSubmit(): Promise<void> {
    if (this.feedbackForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';
      
      const formData = this.feedbackForm.value;
      
      try {
        // Create feedback payload
        const feedbackPayload: FeedbackPayload = {
          type: formData.type,
          title: formData.title,
          description: formData.description,
          screenshot: formData.includeScreenshot ? this.screenshotData : null,
          consoleLogs: formData.includeConsoleLog ? this.capturedLogs : undefined
        };

        // Submit feedback through the service
        await this.feedbackService.submitFeedback(feedbackPayload);
        
        // Reset form and close widget
        this.resetForm();
        this.isWidgetOpen = false;
      } catch (error) {
        console.error('Error submitting feedback:', error);
        this.errorMessage = 'Failed to submit feedback. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  private formatDescription(formData: any): string {
    let description = formData.description + '\n\n';
    
    if (this.capturedLogs.length > 0) {
      description += '--------------------\nConsole Logs:\n--------------------\n';
      this.capturedLogs.forEach(log => {
        const timestamp = new Date(log.timestamp).toLocaleString();
        description += `[${timestamp}] [${log.type.toUpperCase()}] ${log.data}\n`;
      });
    }

    if (this.screenshotData) {
      description += '\n\n--------------------\nScreenshot attached';
    }

    return description;
  }

  private mapTypeToJiraIssueType(type: string): string {
    switch (type) {
      case 'bug':
        return 'Bug';
      case 'feature':
        return 'New Feature';
      case 'improvement':
        return 'Improvement';
      default:
        return 'Task';
    }
  }

  private resetForm() {
    this.feedbackForm.reset();
    this.screenshotData = null;
    this.stopCapturingConsoleLogs();
  }
} 